import { useCallback, useId, useState } from 'react';

import * as css from './SwitchGroup.css';
import { SwitchPill } from './SwitchPill';

export interface SwitchOption<K extends string = string> {
    value: K;
    label: string;
}

/**
 * Represents a single selectable option inside `SwitchGroup`.
 */
export interface SwitchOption<K extends string = string> {
    /**
     * Unique value identifying the option.
     * This is the value returned in `onClick`.
     */
    value: K;

    /**
     * Visible label rendered inside the switch pill.
     */
    label: string;
}

/**
 * Props for `SwitchGroup`.
 *
 * Supports both controlled and uncontrolled usage:
 * - Controlled: provide `value`
 * - Uncontrolled: provide `defaultValue`
 */
export interface SwitchGroupProps<T extends readonly SwitchOption[]> {
    /**
     * List of available switch options.
     * Each option must have a unique `value`.
     */
    options: T;

    /**
     * Currently selected value (controlled mode).
     * When provided, the component does not manage its own state.
     */
    value?: T[number]['value'];

    /**
     * Initial selected value (uncontrolled mode).
     * Ignored if `value` is provided.
     */
    defaultValue?: T[number]['value'];

    /**
     * Called when a switch option is selected.
     * Always fired, whether the component is controlled or uncontrolled.
     */
    onClick?: (activeKey: T[number]['value']) => void;

    /**
     * Alignment of the switch group within its parent layout.
     *
     * @default 'flex-start'
     */
    alignSelf?: 'flex-start' | 'center';

    /**
     * Applies muted styling to the switch group.
     */
    isMuted?: boolean;
}

export function SwitchGroup<T extends readonly SwitchOption[]>({
    options,
    value: controlledValue,
    defaultValue,
    onClick,
    alignSelf = 'flex-start',
    isMuted,
}: SwitchGroupProps<T>) {
    const isControlled = controlledValue !== undefined;

    const [internalValue, setInternalValue] = useState<T[number]['value'] | undefined>(defaultValue);

    const uniqueId = useId();

    const handleChange = useCallback(
        (value: T[number]['value']) => {
            if (!isControlled) {
                setInternalValue(value);
            }
            onClick?.(value);
        },
        [isControlled, onClick],
    );
    const value = isControlled ? controlledValue : internalValue;
    return (
        <div className={css.switchGroupLayout({ alignSelf, isMuted })}>
            {options.map((option, index) => (
                <SwitchPill
                    key={option.value}
                    isActive={option.value === value}
                    value={option.label}
                    selectedKey={option.value}
                    index={index}
                    handleClick={handleChange}
                    uniqueId={uniqueId}
                />
            ))}
        </div>
    );
}
